import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RfUiDatepicker, RfUiInput } from 'renfe-components';
import { createStructuredSelector } from 'reselect';
import {
  setStartDateTimeSelectedPreparation,
  setStartTimeSelectedPreparationPristine,
} from 'store/app/actions';
import {
  makeSelectFinishTimeStampSelectedJourney,
  makeSelectStartDateTimeSelectedJourney,
  makeSelectStartDateTimeStampSelectedJourney,
  makeSelectStartPreparationSelectedError,
  makeSelectStartTimeSelectedPreparation,
  makeSelectStartTimeStampSelectedJourney,
  makeSelectStartDateTimeSelectedPreparation,
  makeSelectStartTimeSelectedPreparationPristine,
  makeSelectTypePracticeSelected,
  makeSelectDisposicionPracticeSelected,
} from 'store/app/selectors';
import { makeSelectPracticaSeleccionada } from 'store/practicas/selectors';
import { PracticaSeleccionada } from 'store/practicas/types';
import {
  makeSelectIsStudentProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import { timestampToTimeString } from 'utils/dateTimeUtils';
import { language } from 'utils/literals';
import { stateLiteral } from 'utils/statesLiterals';

const nowTimeStamp: number = new Date().getTime();

const selectors = createStructuredSelector({
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  timeStampJourneySelectedFinish: makeSelectFinishTimeStampSelectedJourney(),
  timeStampJourneySelectedStart: makeSelectStartTimeStampSelectedJourney(),
  dateTimeJourneySelectedStart: makeSelectStartDateTimeSelectedJourney(),
  timeSelectedPreparationStart: makeSelectStartTimeSelectedPreparation(),
  dateJourneySelectedStart: makeSelectStartDateTimeStampSelectedJourney(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  startDateTimeSelectedPreparation:
    makeSelectStartDateTimeSelectedPreparation(),
  startTimeSelectedPreparationPristine:
    makeSelectStartTimeSelectedPreparationPristine(),
  typePracticeSelected: makeSelectTypePracticeSelected(),
  disposicionPracticeSelected: makeSelectDisposicionPracticeSelected(),
  startPreparationSelectedError: makeSelectStartPreparationSelectedError(),
  isStudentProfile: makeSelectIsStudentProfile(),
} as any);

export const PreparationTimeContainer = () => {
  const dispatch = useDispatch();
  const {
    practicaSeleccionada,
    timeStampJourneySelectedFinish,
    timeStampJourneySelectedStart,
    startDateTimeSelectedPreparation,
    startTimeSelectedPreparationPristine,
    startPreparationSelectedError,
    isStudentProfile,
    disposicionPracticeSelected,
  } = useSelector<any, any>(selectors);


  const editMode: boolean = useMemo(
    () => !!practicaSeleccionada.CUERPO_FICHA,
    [practicaSeleccionada]
  );

  const calculatedPreparationDisabled: boolean = useMemo(() => {
    const tipoPractica = (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA?.ID_TIPO_PRACTICA;
    if (editMode && tipoPractica === 'CT') {
        return false;
     }
    return ['S'].indexOf(disposicionPracticeSelected) > -1 ? false : true;
  }, [disposicionPracticeSelected,practicaSeleccionada]);


  const calculatedStartDateSelectedPreparation: string | undefined =
    useMemo(() => {
      if (startDateTimeSelectedPreparation) {
        const date = new Date(startDateTimeSelectedPreparation);
        date.setHours(0, 0, 0, 0);
        return date.getTime().toString();
      }
    }, [startDateTimeSelectedPreparation]);

  const calculatedStartTimeSelectedPreparation: string = useMemo(() => {
    if (startTimeSelectedPreparationPristine) {
      return '--:--';
    }
    return timestampToTimeString(startDateTimeSelectedPreparation);
  }, [startDateTimeSelectedPreparation, startTimeSelectedPreparationPristine]);

  // Condiciones de edición

  const canEditStartDateSelectedPreparation: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !calculatedPreparationDisabled &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada, calculatedPreparationDisabled]);

  // Eventos

  const handleChangeDateSelectedPreparation = (e: CustomEvent) => {
    const newDate = startDateTimeSelectedPreparation
      ? new Date(startDateTimeSelectedPreparation)
      : new Date();
    const inputDate = new Date(e.detail);
    newDate.setFullYear(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    dispatch(setStartDateTimeSelectedPreparation(newDate));
  };

  const handleChangeTimeSelectedPreparation = (e: CustomEvent) => {
    const newDate = startDateTimeSelectedPreparation
      ? new Date(startDateTimeSelectedPreparation)
      : new Date();
    const data = e.detail ? e.detail.split(':') : null;
    if (data?.length === 2) {
      dispatch(setStartTimeSelectedPreparationPristine(false));
      const [hoursStr, minutesStr] = data;
      newDate.setHours(parseInt(hoursStr), parseInt(minutesStr), 59, 999);
    } else {
      dispatch(setStartTimeSelectedPreparationPristine(true));
      newDate.setHours(0, 0, 0, 0);
    }
    dispatch(setStartDateTimeSelectedPreparation(newDate));
  };

  return (
    <>
      {editMode ? (
        // Detail Practice
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.puestaDisposicion}
          </h2>
          <div>
            <div className="rf-box__child">
              <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
                <RfUiDatepicker
                  moment-locale="es"
                  position={'left'}
                  disabled={!canEditStartDateSelectedPreparation}
                  defaultMaxDateFrom={
                    timeStampJourneySelectedFinish || nowTimeStamp
                  }
                  defaultMinDateFrom={timeStampJourneySelectedStart}
                  labelInput={language.informacionPractica.dateDesdeLabel}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeDateSelectedPreparation}
                  placeholder={
                    language.informacionPractica.hourInicioPlaceholderLabel
                  }
                  defaultDateFrom={calculatedStartDateSelectedPreparation}
                />
              </div>
            </div>

            <div className="rf-box__child">
              <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
                <RfUiInput
                  label={language.informacionPractica.hourInicioLabel}
                  error=""
                  disabled={!canEditStartDateSelectedPreparation}
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeTimeSelectedPreparation}
                  placeholder="XX:XX"
                  textError={startPreparationSelectedError}
                  value={calculatedStartTimeSelectedPreparation}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Create Practice
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.puestaDisposicion}
          </h2>
          <div>
            <div className="rf-box__child">
              <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
                <RfUiDatepicker
                  moment-locale="es"
                  position={'left'}
                  disabled={calculatedPreparationDisabled}
                  defaultMaxDateFrom={
                    timeStampJourneySelectedFinish || nowTimeStamp
                  }
                  defaultMinDateFrom={timeStampJourneySelectedStart}
                  labelInput={language.informacionPractica.dateDesdeLabel}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeDateSelectedPreparation}
                  placeholder={
                    language.informacionPractica.hourInicioPlaceholderLabel
                  }
                  defaultDateFrom={calculatedStartDateSelectedPreparation}
                />
              </div>
            </div>

            <div className="rf-box__child">
              <div className="rf-practice-create__input-container">
                <RfUiInput
                  label={language.informacionPractica.hourInicioLabel}
                  error=""
                  disabled={calculatedPreparationDisabled}
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeTimeSelectedPreparation}
                  placeholder="XX:XX"
                  textError={startPreparationSelectedError}
                  value={calculatedStartTimeSelectedPreparation}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
